import React from 'react'

import { Formik } from 'formik'
import { useHistory } from 'react-router-dom'

import { AdminContext } from 'contexts/AdminContext'
import { ZariContext } from '_zari/context'

import { Divider, Button, Form, Col, Row } from '_zari/core'

import {
  CodigosTransaccionesConstant,
  TiposDocumentoIdentificacionConstant,
  TiposDocumentoSunatConstant,
  LadosIsla,
  ModalidadCalculoIsla
} from 'constants/index'

import * as GuiasVentaService from 'services/guiasVenta.service'
import * as SocioService from 'services/socio.service'

import { DownloadAxiosFile } from 'utils'

import { RetrocederButton } from 'sharedComponents/buttons'

import * as FormGroups from './formGroups'
import * as Sections from './sections'

import { ValidationSchema } from './validation'

const NuevaMainForm = ({ editarData, reiniciarFormulario }) => {
  const __adminContext = React.useContext(AdminContext)
  const __zariContext = React.useContext(ZariContext)

  const history = useHistory()

  const mainFormikRef = React.useRef(null)
  const mainFormDataRef = React.useRef(null)
  const idClienteVarios = React.useRef(null)

  const [datosCombustibleEnUso, setDatosCombustibleEnUso] = React.useState(
    editarData.detalles
      ? editarData.detalles.map(x => ({
          _idProducto: x._idProducto,
          _idProductoPresentacion: x._idProductoPresentacion,
          cantidad: x.cantidad,
          precio: x.precio,
          total: x.total
        }))[0]
      : {}
  )

  const [idListaPrecio, setIdListaPrecio] = React.useState(
    editarData._idListaPrecio || __adminContext.valoresConfiguracionSede._idListaPrecio || ''
  )

  React.useEffect(() => {
    SocioService.obtenerIdClienteVarios()
      .then(({ data }) => {
        idClienteVarios.current = data
      })
      .catch(({ data: { error } }) => __zariContext.snack('error', error))
  }, [__zariContext])

  const formInitialValues = React.useMemo(() => {
    let tipoDocumento = editarData.tipoDocumento || ''
    let serieDocumento = editarData.serieDocumento || ''
    let numeroDocumento = editarData.numeroDocumento || ''

    if (editarData._idDocumentoNota) {
      tipoDocumento = TiposDocumentoSunatConstant.NOTA_CREDITO
      serieDocumento = ''
      numeroDocumento = ''
    }

    return {
      _id: editarData._id || '',
      _idDireccionSocio: editarData._idDireccionSocio || '',
      _idDocumentoRelacionado: editarData._idDocumentoRelacionado ?? '',
      _idPlacaSocio: editarData._idPlacaSocio || '',
      _idSocio: editarData._idSocio || '',
      codigoSocio: editarData.codigoSocio || '',
      comentario: editarData.comentario || '',
      documentoSocio: editarData.documentoSocio || '',
      fechaEmision: editarData.fechaEmision || __adminContext.fechaTrabajo,
      flagGrifo: true,
      ladoIsla: editarData.ladoIsla || LadosIsla.Lado1,
      modalidadCalculoIsla: editarData.modalidadCalculoIsla || ModalidadCalculoIsla.MONTO_TOTAL,
      nombreSocio: editarData.nombreSocio || '',
      numeroDocumento: numeroDocumento,
      serieDocumento: serieDocumento,
      tipoDocumento: tipoDocumento,
      tipoDocumentoSocio: editarData.tipoDocumentoSocio || ''
    }
  }, [editarData, __adminContext.fechaTrabajo])

  const submitFormularioPagos = React.useCallback(
    (formData, { setSubmitting } = {}) => {
      if (!formData._idSocio) {
        formData._idSocio = idClienteVarios.current
      }
      formData._idListaPrecio = idListaPrecio

      if (formData.tipoDocumento === TiposDocumentoSunatConstant.OTROS) {
        if (formData._idSocio === idClienteVarios.current) {
          __zariContext.snack('error', 'Los vales necesitan estas asignados a un cliente.')
          setSubmitting(false)
        }
      }

      const accion = formData._id
        ? () => GuiasVentaService.actualizarGuia(formData._id, formData)
        : () => GuiasVentaService.nuevaGuia(formData)

      accion()
        .then(({ data: data2 }) => {
          if (formData._id) {
            history.push(`/${CodigosTransaccionesConstant.VENTASINGRESOGUIA_LISTA}`)
          } else {
            GuiasVentaService.descargarPdfTicketxId(data2.hash)
              .then(({ data, headers }) => {
                DownloadAxiosFile.downloadAsBlob(data, headers)
                reiniciarFormulario()
              })
              .catch(({ data: { error } }) => __zariContext.snack('error', error))
          }

          __zariContext.toast('success', 'El documento se ha guardado.')
        })
        .catch(({ data: { error } }) => {
          __zariContext.snack('error', error)
          setSubmitting(false)
        })
    },
    [__zariContext, history, reiniciarFormulario, idListaPrecio]
  )

  const onSubmit = React.useCallback(
    formData => {
      const cantidad = parseFloat(datosCombustibleEnUso.cantidad || 0)
      const precio = parseFloat(datosCombustibleEnUso.precio || 0)
      const montoTotal = parseFloat(datosCombustibleEnUso.total || 0)

      const detallesNormalizados = [
        {
          _idProductoPresentacion: datosCombustibleEnUso._idProductoPresentacion,
          cantidad: cantidad,
          precio: precio,
          subTotal: montoTotal
        }
      ]

      const submitData = {
        ...formData,
        numeroDocumento: parseFloat(formData.numeroDocumento),
        detalles: detallesNormalizados
      }

      if (
        submitData.tipoDocumento === TiposDocumentoSunatConstant.BOLETA &&
        submitData.tipoDocumentoSocio !== TiposDocumentoIdentificacionConstant.DNI &&
        submitData.importeTotal > 700
      ) {
        __zariContext.snack('error', 'Boleta con monto mayor a 700 debe ser asignado a un DNI.')
        return
      }

      mainFormDataRef.current = submitData

      submitFormularioPagos(mainFormDataRef.current)
    },
    [__zariContext, submitFormularioPagos, datosCombustibleEnUso]
  )

  return (
    <Row>
      <Col md={12}>
        <Formik
          initialValues={formInitialValues}
          innerRef={mainFormikRef}
          onSubmit={onSubmit}
          validationSchema={ValidationSchema}
          validateOnMount={false}
          validateOnChange={false}
          validateOnBlur={false}
        >
          {({ errors, isSubmitting, setValues, touched, values, setFieldValue, handleSubmit, handleChange }) => (
            <>
              <Form>
                <Row>
                  <Col md={5}>
                    <FormGroups.SocioFormGroup
                      iValue={values._idSocio}
                      values={values}
                      setValues={setValues}
                      iError={errors._idSocio}
                    />
                    <FormGroups.DireccionFormGroup
                      iError={errors._idDireccionSocio}
                      iTouched={touched._idDireccionSocio}
                      vIdSocio={values._idSocio}
                      fIsSubmitting={isSubmitting}
                      setFieldValue={setFieldValue}
                    />
                    <Row>
                      <Col md={6}>
                        <FormGroups.FechaEmisionFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.fechaEmision}
                          iTouched={touched.fechaEmision}
                          handleChange={handleChange}
                          setValues={setValues}
                          values={values}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                      <Col md={6}>
                        <FormGroups.TipoDocumentoFormGroup
                          fIsSubmitting={isSubmitting}
                          iError={errors.tipoDocumento}
                          iTouched={touched.tipoDocumento}
                          vIdDocumentoNota={values._idDocumentoNota}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <FormGroups.PlacaSocioFormGroup
                          values={values}
                          setValues={setValues}
                          iError={errors._idPlacaSocio}
                          iTouched={touched._idPlacaSocio}
                          iValue_IdSocio={values._idSocio}
                          fIsSubmitting={isSubmitting}
                          flagGrifo={values.flagGrifo}
                        />
                      </Col>
                      {values.tipoDocumento === TiposDocumentoSunatConstant.OTROS && (
                        <Col md={6}>
                          <FormGroups.SerieNumeroFormGroup
                            fIsSubmitting={isSubmitting}
                            setFieldValue={setFieldValue}
                            vId={values._id}
                            vTipoDocumento={values.tipoDocumento}
                            vSerieDocumento={values.serieDocumento}
                            flagGrifo={values.flagGrifo}
                            vSerieReferencia={editarData.serieDocumento}
                          />
                        </Col>
                      )}
                    </Row>
                  </Col>
                  <Col md={5} style={{ borderLeft: '1px solid black' }}>
                    <FormGroups.ModalidadCalculoIslaFormGroup
                      iValue={values.modalidadCalculoIsla}
                      fIsSubmitting={isSubmitting}
                    />
                    <Divider />
                    <Sections.CombustiblesxIslaSection
                      modalidadCalculo={values.modalidadCalculoIsla}
                      datosCombustibleEnUso={datosCombustibleEnUso}
                      setDatosCombustibleEnUso={setDatosCombustibleEnUso}
                      ladoSeleccionado={values.ladoIsla}
                      idListaPrecio={idListaPrecio}
                      setFieldValue={setFieldValue}
                      handleSubmit={handleSubmit}
                      flagEdicion={!!values._id}
                    />
                    <Divider />
                    <Form.Group>
                      <Form.Label text='TOTAL' md={8} alignRightAt='md' notSlim />
                      <Col md={4}>
                        <Form.InputNumber
                          id='montoTotal'
                          name='montoTotal'
                          disabled
                          placeholder={datosCombustibleEnUso.total || '0.00'}
                        />
                      </Col>
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    {!__adminContext.valoresConfiguracionSede._idListaPrecio && (
                      <FormGroups.IdListaPrecioFormGroup
                        fIsSubmitting={isSubmitting}
                        idListaPrecio={idListaPrecio}
                        setIdListaPrecio={setIdListaPrecio}
                        flagEdicion={!!values._id}
                      />
                    )}
                  </Col>
                </Row>
                <Divider noMargin />
                <Row>
                  <Col md={12} className='text-right'>
                    <RetrocederButton />
                    <Button disabled={isSubmitting} onClick={handleSubmit}>
                      Cobrar
                    </Button>
                  </Col>
                </Row>
              </Form>
            </>
          )}
        </Formik>
      </Col>
    </Row>
  )
}

export default NuevaMainForm
